import { useEffect, useMemo, useRef, useState } from "react";
import {
    GetAllProductPrice,
    GetProductPriceById,
    GetProductPriceByProductId,
    AddProductPrice,
    UpdateProductPrice,
} from "../services/ProductPriceService";
import { GetActiveProduct } from "../services/ProductService";
import { useLanguage } from "./LanguageContext"; // مسیر واقعی خودت
import ExcelJS from "exceljs";

/* -------------------------------------------------------------------------- */
/*  فونت پایه                                                                  */
/* -------------------------------------------------------------------------- */
const FONT = "IRANSansWeb, Vazirmatn, sans-serif";

/* -------------------------------------------------------------------------- */
/*  متن‌ها                                                                     */
/* -------------------------------------------------------------------------- */
const TEXTS = {
    fa: {
        dir: "rtl",
        title: "مدیریت قیمت محصولات",
        subtitle: "تاریخچه و اعلامیه‌های قیمت محصولات را مشاهده و مدیریت کنید",
        searchPlaceholder: "جستجو با نام محصول یا توضیحات...",
        addNew: "افزودن اعلامیه قیمت",
        edit: "ویرایش",
        product: "محصول",
        price: "قیمت",
        fromDate: "تاریخ شروع",
        toDate: "تاریخ پایان",
        description: "توضیحات",
        status: "وضعیت",
        current: "قیمت جاری",
        expired: "منقضی‌شده",
        filterAll: "همه",
        save: "ذخیره",
        cancel: "انصراف",
        modalAddTitle: "افزودن اعلامیه قیمت جدید",
        modalEditTitle: "ویرایش اعلامیه قیمت",
        loading: "در حال بارگذاری...",
        saving: "در حال ذخیره...",
        empty: "هنوز هیچ اعلامیه قیمتی ثبت نشده است",
        emptyAction: "افزودن اولین اعلامیه قیمت",
        noSearchResult: "نتیجه‌ای یافت نشد",
        toastProductRequired: "محصول را انتخاب کنید",
        toastPriceRequired: "قیمت را به‌درستی وارد کنید",
        toastFromDateRequired: "تاریخ شروع را وارد کنید",
        toastFromDatePast: "تاریخ شروع نمی‌تواند قبل از امروز باشد",
        toastCannotEditExpired: "اعلامیه‌های منقضی‌شده قابل ویرایش نیستند",
        toastCannotEditNotToday: "این اعلامیه فقط در همان روزی که ثبت شده قابل ویرایش است",
        toastAddSuccess: "اعلامیه قیمت با موفقیت افزوده شد",
        toastAddFail: "خطا در افزودن اعلامیه قیمت",
        toastUpdateSuccess: "اعلامیه قیمت با موفقیت ویرایش شد",
        toastUpdateFail: "خطا در ویرایش اعلامیه قیمت",
        toastFetchFail: "خطا در دریافت لیست قیمت‌ها",
        toastProductsFetchFail: "خطا در دریافت لیست محصولات",
        toastDateOrderInvalid: "تاریخ شروع اعلامیه قیمت جدید باید بعد از اعلامیه قبلی باشد",
        toastDuplicateSameDay: "برای این محصول، اعلامیه قیمت دیگری با همین تاریخ شروع ثبت شده است",
        toastSameDayRange: "تاریخ شروع و پایان یک اعلامیه نمی‌توانند در یک روز باشند",
        itemsCount: (n) => `${n} اعلامیه`,
        autoToDateHint: "تاریخ پایان این اعلامیه، پس از ثبت قیمت بعدی برای همین محصول به‌صورت خودکار تعیین می‌شود",
        stillCurrent: "بدون تاریخ پایان (جاری)",
        viewCard: "کارت",
        viewList: "فهرست",
        actions: "عملیات",
        noValue: "—",
        selectProduct: "انتخاب محصول...",
        searchProductPlaceholder: "جستجوی محصول...",
        noOptionsFound: "موردی یافت نشد",
        currency: "دینار",
        expiredEditTooltip: "اعلامیه‌های منقضی‌شده قابل ویرایش نیستند",
        notTodayEditTooltip: "این اعلامیه فقط در همان روزی که ثبت شده قابل ویرایش است",
        exportExcel: "خروجی اکسل",
        exportingExcel: "در حال ساخت فایل...",
        noPriceReportTitle: "کالاهای بدون قیمت جاری",
        noPriceReportDesc: (n) => `${n} محصول فاقد قیمت جاری هستند`,
        noPriceReportNone: "همه محصولات قیمت جاری دارند",
        exportNoPriceExcel: "خروجی اکسل کالاهای بدون قیمت",
        neverPriced: "هیچ‌گاه قیمت‌گذاری نشده",
        lastPriceExpiredOn: "آخرین قیمت منقضی‌شده",
        excelSheetPrices: "اعلامیه‌های قیمت",
        excelSheetNoPrice: "کالاهای بدون قیمت",
        excelColProduct: "نام محصول",
        excelColCode: "کد محصول",
        excelColPrice: "قیمت",
        excelColFromDate: "تاریخ شروع",
        excelColToDate: "تاریخ پایان",
        excelColDescription: "توضیحات",
        excelColStatus: "وضعیت",
        excelColLastPriceInfo: "وضعیت قیمت",
        excelColLastPriceDate: "تاریخ آخرین قیمت",
        excelGeneratedAt: "تاریخ تولید گزارش",
        rowsPerPage: "تعداد در هر صفحه",
        prev: "قبلی",
        next: "بعدی",
        showingRange: (from, to, total) => `نمایش ${from} تا ${to} از ${total} مورد`,
    },
    ar: {
        dir: "rtl",
        title: "إدارة أسعار المنتجات",
        subtitle: "عرض وإدارة سجل وإعلانات أسعار المنتجات",
        searchPlaceholder: "بحث باسم المنتج أو الوصف...",
        addNew: "إضافة إعلان سعر",
        edit: "تعديل",
        product: "المنتج",
        price: "السعر",
        fromDate: "تاريخ البدء",
        toDate: "تاريخ الانتهاء",
        description: "الوصف",
        status: "الحالة",
        current: "السعر الحالي",
        expired: "منتهي",
        filterAll: "الكل",
        save: "حفظ",
        cancel: "إلغاء",
        modalAddTitle: "إضافة إعلان سعر جديد",
        modalEditTitle: "تعديل إعلان السعر",
        loading: "جارٍ التحميل...",
        saving: "جارٍ الحفظ...",
        empty: "لم يتم تسجيل أي إعلان سعر بعد",
        emptyAction: "إضافة أول إعلان سعر",
        noSearchResult: "لا توجد نتائج",
        toastProductRequired: "اختر المنتج",
        toastPriceRequired: "أدخل السعر بشكل صحيح",
        toastFromDateRequired: "أدخل تاريخ البدء",
        toastFromDatePast: "لا يمكن أن يكون تاريخ البدء قبل اليوم",
        toastCannotEditExpired: "لا يمكن تعديل الإعلانات المنتهية",
        toastCannotEditNotToday: "لا يمكن تعديل هذا الإعلان إلا في نفس يوم تسجيله",
        toastAddSuccess: "تمت إضافة إعلان السعر بنجاح",
        toastAddFail: "خطأ في إضافة إعلان السعر",
        toastUpdateSuccess: "تم تعديل إعلان السعر بنجاح",
        toastUpdateFail: "خطأ في تعديل إعلان السعر",
        toastFetchFail: "خطأ في جلب قائمة الأسعار",
        toastProductsFetchFail: "خطأ في جلب قائمة المنتجات",
        toastDateOrderInvalid: "يجب أن يكون تاريخ بدء الإعلان الجديد بعد الإعلان السابق",
        toastDuplicateSameDay: "يوجد إعلان سعر آخر لهذا المنتج بنفس تاريخ البدء",
        toastSameDayRange: "لا يمكن أن يكون تاريخ البدء والانتهاء لإعلان واحد في نفس اليوم",
        itemsCount: (n) => `${n} إعلان`,
        autoToDateHint: "سيتم تحديد تاريخ انتهاء هذا الإعلان تلقائياً عند تسجيل السعر التالي لنفس المنتج",
        stillCurrent: "بدون تاريخ انتهاء (حالي)",
        viewCard: "بطاقة",
        viewList: "قائمة",
        actions: "إجراءات",
        noValue: "—",
        selectProduct: "اختر المنتج...",
        searchProductPlaceholder: "بحث عن منتج...",
        noOptionsFound: "لا توجد نتائج",
        currency: "ريال",
        expiredEditTooltip: "لا يمكن تعديل الإعلانات المنتهية",
        notTodayEditTooltip: "لا يمكن تعديل هذا الإعلان إلا في نفس يوم تسجيله",
        exportExcel: "تصدير إلى إكسل",
        exportingExcel: "جارٍ إنشاء الملف...",
        noPriceReportTitle: "منتجات بدون سعر حالي",
        noPriceReportDesc: (n) => `${n} منتج بدون سعر حالي`,
        noPriceReportNone: "جميع المنتجات لديها سعر حالي",
        exportNoPriceExcel: "تصدير المنتجات بدون سعر",
        neverPriced: "لم يتم تحديد سعر له مطلقاً",
        lastPriceExpiredOn: "انتهى آخر سعر في",
        excelSheetPrices: "إعلانات الأسعار",
        excelSheetNoPrice: "منتجات بدون سعر",
        excelColProduct: "اسم المنتج",
        excelColCode: "كود المنتج",
        excelColPrice: "السعر",
        excelColFromDate: "تاريخ البدء",
        excelColToDate: "تاريخ الانتهاء",
        excelColDescription: "الوصف",
        excelColStatus: "الحالة",
        excelColLastPriceInfo: "حالة السعر",
        excelColLastPriceDate: "تاريخ آخر سعر",
        excelGeneratedAt: "تاريخ إنشاء التقرير",
        rowsPerPage: "عدد العناصر في الصفحة",
        prev: "السابق",
        next: "التالي",
        showingRange: (from, to, total) => `عرض ${from} إلى ${to} من ${total} عنصر`,
    },
};

/* -------------------------------------------------------------------------- */
/*  تقویم شمسی (جلالی) — تبدیل میلادی <-> شمسی                                */
/*  الگوریتم استاندارد و رایج (بدون نیاز به کتابخانه خارجی)                    */
/* -------------------------------------------------------------------------- */
function gregorianToJalali(gy, gm, gd) {
    const g_d_m = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
    let jy = gy <= 1600 ? 0 : 979;
    gy -= gy <= 1600 ? 621 : 1600;
    const gy2 = gm > 2 ? gy + 1 : gy;
    let days =
        365 * gy +
        Math.floor((gy2 + 3) / 4) -
        Math.floor((gy2 + 99) / 100) +
        Math.floor((gy2 + 399) / 400) -
        80 +
        gd +
        g_d_m[gm - 1];
    jy += 33 * Math.floor(days / 12053);
    days %= 12053;
    jy += 4 * Math.floor(days / 1461);
    days %= 1461;
    if (days > 365) {
        jy += Math.floor((days - 1) / 365);
        days = (days - 1) % 365;
    }
    const jm = days < 186 ? 1 + Math.floor(days / 31) : 7 + Math.floor((days - 186) / 30);
    const jd = 1 + (days < 186 ? days % 31 : (days - 186) % 30);
    return [jy, jm, jd];
}

function jalaliToGregorian(jy, jm, jd) {
    let gy = jy <= 979 ? 621 : 1600;
    jy -= jy <= 979 ? 0 : 979;
    let days =
        365 * jy +
        Math.floor(jy / 33) * 8 +
        Math.floor(((jy % 33) + 3) / 4) +
        78 +
        jd +
        (jm < 7 ? (jm - 1) * 31 : (jm - 7) * 30 + 186);
    gy += 400 * Math.floor(days / 146097);
    days %= 146097;
    if (days > 36524) {
        gy += 100 * Math.floor(--days / 36524);
        days %= 36524;
        if (days >= 365) days++;
    }
    gy += 4 * Math.floor(days / 1461);
    days %= 1461;
    if (days > 365) {
        gy += Math.floor((days - 1) / 365);
        days = (days - 1) % 365;
    }
    let gd = days + 1;
    const isLeapGYear = (y) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
    const sal_a = [0, 31, isLeapGYear(gy) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    let gm = 1;
    for (gm = 1; gm <= 12; gm += 1) {
        if (gd <= sal_a[gm]) break;
        gd -= sal_a[gm];
    }
    return [gy, gm, gd];
}

function isoToJalali(iso) {
    const [gy, gm, gd] = iso.split("-").map(Number);
    return gregorianToJalali(gy, gm, gd);
}

function jalaliMonthLength(jy, jm) {
    if (jm <= 6) return 31;
    if (jm <= 11) return 30;
    // اسفند: بررسی کبیسه بودن سال با گرد کردن رفت‌وبرگشتی
    const [gy, gm, gd] = jalaliToGregorian(jy, 12, 30);
    const [jy2, jm2, jd2] = gregorianToJalali(gy, gm, gd);
    return jy2 === jy && jm2 === 12 && jd2 === 30 ? 30 : 29;
}

const JALALI_MONTHS = [
    "فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور",
    "مهر", "آبان", "آذر", "دی", "بهمن", "اسفند",
];
const JALALI_WEEKDAYS = ["ش", "ی", "د", "س", "چ", "پ", "ج"]; // شنبه تا جمعه

function pad2(n) {
    return String(n).padStart(2, "0");
}

/* -------------------------------------------------------------------------- */
/*  تاریخ امروز (میلادی yyyy-mm-dd) — برای جلوگیری از ثبت تاریخ گذشته          */
/* -------------------------------------------------------------------------- */
function getTodayISO() {
    const now = new Date();
    const y = now.getFullYear();
    const m = pad2(now.getMonth() + 1);
    const d = pad2(now.getDate());
    return `${y}-${m}-${d}`;
}

/* -------------------------------------------------------------------------- */
/*  آیکن‌ها                                                                    */
/* -------------------------------------------------------------------------- */
function Icon({ name, size = 14, color = "currentColor" }) {
    const paths = {
        plus: <><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>,
        edit: <><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></>,
        search: <><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>,
        close: <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>,
        chevronDown: <polyline points="6 9 12 15 18 9" />,
        tag: <><path d="M20.59 13.41 11 3.83A2 2 0 0 0 9.59 3.17L4 3a1 1 0 0 0-1 1l.17 5.59a2 2 0 0 0 .66 1.41l9.58 9.58a2 2 0 0 0 2.83 0l4.35-4.35a2 2 0 0 0 0-2.82Z" /><circle cx="7.5" cy="7.5" r="1.2" /></>,
        calendar: <><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>,
        check: <polyline points="20 6 9 17 4 12" />,
        alert: <><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></>,
        grid: <><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /></>,
        list: <><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></>,
        box: <><path d="M21 8a1 1 0 0 0-.5-.87l-8-4.5a1 1 0 0 0-1 0l-8 4.5A1 1 0 0 0 3 8v8a1 1 0 0 0 .5.87l8 4.5a1 1 0 0 0 1 0l8-4.5A1 1 0 0 0 21 16Z" /><path d="M3.27 7.96 12 12.75l8.73-4.79" /><path d="M12 22.08V12.75" /></>,
        chevronRight: <polyline points="9 18 15 12 9 6" />,
        chevronLeft: <polyline points="15 18 9 12 15 6" />,
        lock: <><rect x="4.5" y="10.5" width="15" height="9.5" rx="2" /><path d="M7.5 10.5V7a4.5 4.5 0 0 1 9 0v3.5" /></>,
        download: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></>,
        sortNeutral: <><path d="M8 9l4-4 4 4" /><path d="M16 15l-4 4-4-4" /></>,
        sortAsc: <path d="M6 15l6-6 6 6" />,
        sortDesc: <path d="M6 9l6 6 6-6" />,
        alertTriangle: <><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>,
    };
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
            fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
            {paths[name] ?? null}
        </svg>
    );
}

/* -------------------------------------------------------------------------- */
/*  Toast (بالای صفحه)                                                        */
/* -------------------------------------------------------------------------- */
function Toast({ message, visible, type }) {
    if (!visible) return null;
    return (
        <div style={{
            position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)",
            background: type === "error" ? "#EF4444" : "#10B981",
            color: "#fff", padding: "10px 20px", borderRadius: 12,
            fontSize: 12, fontWeight: 500, zIndex: 3000,
            display: "flex", alignItems: "center", gap: 8,
            fontFamily: FONT,
            boxShadow: type === "error" ? "0 8px 24px rgba(239,68,68,.3)" : "0 8px 24px rgba(16,185,129,.3)",
            animation: "toastDrop .25s ease both",
        }}>
            <Icon name={type === "error" ? "alert" : "check"} size={13} color="#fff" />
            {message}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  فیلدهای فرم                                                                */
/* -------------------------------------------------------------------------- */
const fieldBaseStyle = {
    width: "100%", height: 40, border: "1.5px solid #E8ECF4",
    borderRadius: 10, padding: "0 12px", fontSize: 12,
    fontFamily: FONT, direction: "rtl",
    outline: "none", color: "#1E293B", boxSizing: "border-box",
    background: "#F8FAFF", transition: "all .15s",
};

const calNavBtnStyle = {
    width: 24, height: 24, borderRadius: 7, border: "none",
    background: "#F1F5F9", color: "#6C3DFF", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontFamily: FONT,
};

function TextField({ value, onChange, placeholder, autoFocus, hasError, type = "text", step, min, disabled, dir }) {
    return (
        <input
            autoFocus={autoFocus}
            type={type}
            step={step}
            min={min}
            disabled={disabled}
            dir={dir}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            style={{
                ...fieldBaseStyle,
                ...(disabled ? { opacity: 0.6, cursor: "not-allowed" } : {}),
                ...(hasError ? { border: "1.5px solid #EF4444", background: "#FEF2F2" } : {}),
            }}
            onFocus={(e) => { e.target.style.borderColor = hasError ? "#EF4444" : "#6C3DFF"; e.target.style.background = "#fff"; e.target.style.boxShadow = hasError ? "0 0 0 4px rgba(239,68,68,.08)" : "0 0 0 4px rgba(108,61,255,.08)"; }}
            onBlur={(e) => { e.target.style.borderColor = hasError ? "#EF4444" : "#E8ECF4"; e.target.style.background = hasError ? "#FEF2F2" : "#F8FAFF"; e.target.style.boxShadow = "none"; }}
        />
    );
}

/* -------------------------------------------------------------------------- */
/*  انتخاب‌گر تاریخ شمسی (برای زبان فارسی)                                     */
/*  مقدار ورودی/خروجی همچنان به‌صورت رشته‌ی میلادی yyyy-mm-dd است تا با         */
/*  ساختار داده و بک‌اند فعلی سازگار بماند؛ فقط نمایش آن شمسی است.             */
/*  minDate (اختیاری، رشته‌ی میلادی yyyy-mm-dd): روزهای قبل از آن غیرفعال      */
/*  می‌شوند (برای جلوگیری از انتخاب تاریخ گذشته).                              */
/* -------------------------------------------------------------------------- */
function JalaliDatePicker({ value, onChange, disabled, hasError, placeholder, minDate }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    const todayJ = useMemo(() => {
        const now = new Date();
        return gregorianToJalali(now.getFullYear(), now.getMonth() + 1, now.getDate());
    }, []);
    const selectedJ = value ? isoToJalali(value) : null;
    const minJ = minDate ? isoToJalali(minDate) : null;

    const [viewYear, setViewYear] = useState((selectedJ || minJ || todayJ)[0]);
    const [viewMonth, setViewMonth] = useState((selectedJ || minJ || todayJ)[1]);

    useEffect(() => {
        if (value) {
            const [jy, jm] = isoToJalali(value);
            setViewYear(jy);
            setViewMonth(jm);
        }
    }, [value]);

    useEffect(() => {
        function handleClickOutside(e) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => { if (disabled) setOpen(false); }, [disabled]);

    const displayText = selectedJ ? `${selectedJ[0]}/${pad2(selectedJ[1])}/${pad2(selectedJ[2])}` : "";

    const monthLen = jalaliMonthLength(viewYear, viewMonth);
    const [fgy, fgm, fgd] = jalaliToGregorian(viewYear, viewMonth, 1);
    const firstDayOfWeek = (new Date(fgy, fgm - 1, fgd).getDay() + 1) % 7; // 0 = شنبه

    const cells = [];
    for (let i = 0; i < firstDayOfWeek; i += 1) cells.push(null);
    for (let d = 1; d <= monthLen; d += 1) cells.push(d);

    // آیا امکان رفتن به ماه قبل هست؟ (اگر minDate تعیین شده، نباید بشود قبل از ماه آن رفت)
    const canGoPrevMonth = !minJ || viewYear > minJ[0] || (viewYear === minJ[0] && viewMonth > minJ[1]);

    const goPrevMonth = () => {
        if (!canGoPrevMonth) return;
        let m = viewMonth - 1, y = viewYear;
        if (m < 1) { m = 12; y -= 1; }
        setViewMonth(m); setViewYear(y);
    };
    const goNextMonth = () => {
        let m = viewMonth + 1, y = viewYear;
        if (m > 12) { m = 1; y += 1; }
        setViewMonth(m); setViewYear(y);
    };

    const isDayDisabled = (d) => {
        if (!minJ) return false;
        if (viewYear < minJ[0]) return true;
        if (viewYear > minJ[0]) return false;
        if (viewMonth < minJ[1]) return true;
        if (viewMonth > minJ[1]) return false;
        return d < minJ[2];
    };

    const selectDay = (d) => {
        if (isDayDisabled(d)) return;
        const [gy, gm, gd] = jalaliToGregorian(viewYear, viewMonth, d);
        onChange(`${gy}-${pad2(gm)}-${pad2(gd)}`);
        setOpen(false);
    };

    const clearValue = (e) => {
        e.stopPropagation();
        onChange("");
    };

    return (
        <div ref={wrapRef} style={{ position: "relative" }}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => setOpen((o) => !o)}
                style={{
                    ...fieldBaseStyle,
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.55 : 1,
                    ...(hasError ? { border: "1.5px solid #EF4444", background: "#FEF2F2" } : {}),
                }}
            >
                <span style={{ color: displayText ? "#1E293B" : "#94A3B8", fontWeight: displayText ? 600 : 400, flex: 1, textAlign: "start" }}>
                    {displayText || placeholder || ""}
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {displayText && !disabled && (
                        <span onClick={clearValue} style={{ display: "flex", cursor: "pointer" }}>
                            <Icon name="close" size={11} color="#94A3B8" />
                        </span>
                    )}
                    <Icon name="calendar" size={13} color="#94A3B8" />
                </span>
            </button>

            {open && !disabled && (
                <div style={{
                    position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 60,
                    background: "#fff", border: "1.5px solid #E8ECF4", borderRadius: 10,
                    boxShadow: "0 12px 28px rgba(15,23,42,0.14)", padding: 10, width: 236,
                }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                        <button type="button" onClick={goNextMonth} style={calNavBtnStyle}>
                            <Icon name="chevronLeft" size={13} color="#6C3DFF" />
                        </button>
                        <span style={{ fontSize: 11.5, fontWeight: 700, color: "#1E293B" }}>
                            {JALALI_MONTHS[viewMonth - 1]} {viewYear}
                        </span>
                        <button
                            type="button"
                            onClick={goPrevMonth}
                            disabled={!canGoPrevMonth}
                            style={{ ...calNavBtnStyle, opacity: canGoPrevMonth ? 1 : 0.35, cursor: canGoPrevMonth ? "pointer" : "not-allowed" }}
                        >
                            <Icon name="chevronRight" size={13} color="#6C3DFF" />
                        </button>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 4 }}>
                        {JALALI_WEEKDAYS.map((w, i) => (
                            <span key={i} style={{ fontSize: 9.5, color: "#94A3B8", textAlign: "center", fontWeight: 700 }}>{w}</span>
                        ))}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
                        {cells.map((d, i) => {
                            const isSelected = selectedJ && selectedJ[0] === viewYear && selectedJ[1] === viewMonth && selectedJ[2] === d;
                            const isToday = todayJ[0] === viewYear && todayJ[1] === viewMonth && todayJ[2] === d;
                            const disabledDay = d ? isDayDisabled(d) : false;
                            return (
                                <button
                                    key={i}
                                    type="button"
                                    disabled={!d || disabledDay}
                                    onClick={() => d && selectDay(d)}
                                    style={{
                                        height: 26, border: isToday && !isSelected ? "1px solid #6C3DFF" : "none",
                                        borderRadius: 7,
                                        background: isSelected ? "#6C3DFF" : "transparent",
                                        color: !d ? "transparent" : disabledDay ? "#CBD5E1" : isSelected ? "#fff" : "#1E293B",
                                        fontSize: 10.5, fontWeight: isSelected ? 700 : 500,
                                        cursor: d && !disabledDay ? "pointer" : "default",
                                        fontFamily: FONT, padding: 0,
                                    }}
                                    onMouseEnter={(e) => { if (d && !isSelected && !disabledDay) e.currentTarget.style.background = "#F8FAFF"; }}
                                    onMouseLeave={(e) => { if (d && !isSelected && !disabledDay) e.currentTarget.style.background = "transparent"; }}
                                >
                                    {d || ""}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  دراپ‌داون قابل‌جستجو (استفاده برای محصول)                                   */
/* -------------------------------------------------------------------------- */
function SearchableSelect({ value, onChange, options, placeholder, searchPlaceholder, emptyText, disabled, hasError }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const wrapRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(e) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
                setSearch("");
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (disabled) setOpen(false);
    }, [disabled]);

    const selected = options.find((o) => o.value === value);
    const filtered = search.trim()
        ? options.filter((o) => o.label.toLowerCase().includes(search.trim().toLowerCase()))
        : options;

    return (
        <div ref={wrapRef} style={{ position: "relative" }}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => setOpen((o) => !o)}
                style={{
                    ...fieldBaseStyle,
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.55 : 1,
                    ...(hasError ? { border: "1.5px solid #EF4444", background: "#FEF2F2" } : {}),
                }}
            >
                <span style={{
                    color: selected ? "#1E293B" : "#94A3B8", fontWeight: selected ? 600 : 400,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, textAlign: "start",
                }}>
                    {selected ? selected.label : placeholder}
                </span>
                <Icon name="chevronDown" size={13} color="#94A3B8" />
            </button>

            {open && !disabled && (
                <div style={{
                    position: "absolute", top: "calc(100% + 4px)", right: 0, left: 0, zIndex: 60,
                    background: "#fff", border: "1.5px solid #E8ECF4", borderRadius: 10,
                    boxShadow: "0 12px 28px rgba(15,23,42,0.14)", maxHeight: 230,
                    display: "flex", flexDirection: "column", overflow: "hidden",
                }}>
                    <div style={{ padding: 8, borderBottom: "1px solid #F1F5F9", flexShrink: 0 }}>
                        <div style={{ position: "relative" }}>
                            <input
                                autoFocus
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={searchPlaceholder}
                                style={{ ...fieldBaseStyle, height: 32, fontSize: 11, padding: "0 30px 0 10px" }}
                            />
                            <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)" }}>
                                <Icon name="search" size={12} color="#94A3B8" />
                            </span>
                        </div>
                    </div>
                    <div style={{ overflowY: "auto" }}>
                        {filtered.length === 0 ? (
                            <p style={{ margin: 0, padding: "10px 12px", fontSize: 11, color: "#94A3B8" }}>{emptyText}</p>
                        ) : (
                            filtered.map((opt) => {
                                const isSelected = opt.value === value;
                                return (
                                    <div
                                        key={opt.value}
                                        onClick={() => { onChange(opt.value); setOpen(false); setSearch(""); }}
                                        style={{
                                            padding: "9px 12px", fontSize: 11.5, cursor: "pointer",
                                            background: isSelected ? "#6C3DFF14" : "transparent",
                                            color: isSelected ? "#6C3DFF" : "#1E293B",
                                            fontWeight: isSelected ? 700 : 500,
                                            transition: "background .12s",
                                        }}
                                        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = "#F8FAFF"; }}
                                        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                                    >
                                        {opt.label}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  نرمال‌سازی داده‌ها (منطبق بر Entity/ViewModel واقعی)                        */
/*  اگر پاسخ سرور PascalCase برگرداند هم پوشش داده شده                        */
/* -------------------------------------------------------------------------- */
const normalizeProductPrice = (raw) => ({
    id: raw.id ?? raw.Id,
    productId: raw.productId ?? raw.ProductId,
    productName: raw.productName ?? raw.ProductName ?? "",
    productCode: raw.productCode ?? raw.ProductCode ?? raw.code ?? raw.Code ?? "",
    price: raw.price ?? raw.Price ?? 0,
    fromDate: raw.fromDate ?? raw.FromDate ?? null,
    toDate: raw.toDate ?? raw.ToDate ?? null,
    description: raw.description ?? raw.Description ?? "",
});

const normalizeProduct = (raw) => ({
    id: raw.id ?? raw.Id,
    name: raw.name ?? raw.Name ?? raw.title ?? raw.Title ?? "",
    code: raw.code ?? raw.Code ?? raw.productCode ?? raw.ProductCode ?? raw.sku ?? raw.Sku ?? raw.SKU ?? "",
});

function toInputDate(value) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

/* یک روزِ تقویمی (بدون توجه به ساعت) با روز دیگر برابر است یا نه */
function isSameDay(a, b) {
    if (!a || !b) return false;
    return toInputDate(a) === toInputDate(b);
}

/* اعلامیه قیمت فقط زمانی قابل ویرایش است که «جاری» باشد (بدون تاریخ پایان)
   و تاریخ شروع آن «همان روز جاری» باشد؛ اعلامیه‌های جاریِ روزهای قبل دیگر قابل ویرایش نیستند */
function canEditPriceItem(item) {
    if (!item || item.toDate) return false;
    return isSameDay(item.fromDate, getTodayISO());
}

function formatDisplayDate(value, lang) {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    // فارسی: تقویم شمسی (جلالی) | عربی: همان تقویم میلادی
    return lang === "ar"
        ? d.toLocaleDateString("ar-u-ca-gregory")
        : d.toLocaleDateString("fa-IR-u-ca-persian");
}

function formatPrice(value) {
    const num = Number(value) || 0;
    return num.toLocaleString("en-US");
}
/* جداسازی سه‌رقمی حین تایپ در فیلد قیمت (فقط نمایش؛ مقدار خام بدون کاما نگه داشته می‌شود) */
function formatNumberInput(value) {
    if (value === null || value === undefined) return "";
    const strValue = String(value);
    const parts = strValue.split(".");
    parts[0] = parts[0].replace(/\D/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    if (parts.length > 1) {
        return `${parts[0]}.${parts[1].replace(/\D/g, "")}`;
    }
    return parts[0];
}

function unformatNumberInput(value) {
    return String(value ?? "").replace(/,/g, "");
}

/* -------------------------------------------------------------------------- */
/*  بج وضعیت (جاری / منقضی)                                                    */
/* -------------------------------------------------------------------------- */
function StatusBadge({ isCurrent, t }) {
    return (
        <span style={{
            justifySelf: "start",
            display: "inline-flex", alignItems: "center", gap: 5,
            fontSize: 9.5, fontWeight: 700, padding: "3px 9px", borderRadius: 99,
            background: isCurrent ? "#10B98114" : "#94A3B814",
            color: isCurrent ? "#10B981" : "#94A3B8",
            fontFamily: FONT, whiteSpace: "nowrap",
        }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: isCurrent ? "#10B981" : "#94A3B8" }} />
            {isCurrent ? t.current : t.expired}
        </span>
    );
}

/* -------------------------------------------------------------------------- */
/*  کارت اعلامیه قیمت                                                          */
/* -------------------------------------------------------------------------- */
function PriceCard({ item, onEdit, t, lang }) {
    const isCurrent = !item.toDate;
    const canEdit = canEditPriceItem(item);
    const from = formatDisplayDate(item.fromDate, lang);
    const to = formatDisplayDate(item.toDate, lang);
    return (
        <div
            className="price-card"
            style={{
                background: "#fff", borderRadius: 16, border: "1px solid #EEF2FF",
                padding: 16, display: "flex", flexDirection: "column", gap: 12,
                boxShadow: "0 1px 3px rgba(0,0,0,0.03)", transition: "all .18s",
            }}
        >
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <div style={{
                    width: 40, height: 40, borderRadius: 12, flexShrink: 0,
                    background: "linear-gradient(135deg, #6C3DFF1A, #9B59FF33)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                    <Icon name="box" size={18} color="#6C3DFF" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#1E293B", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {item.productName || t.noValue}
                        {item.productCode && (
                            <span style={{ fontSize: 10, fontWeight: 600, color: "#94A3B8" }}> ({item.productCode})</span>
                        )}
                    </p>
                    <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 3 }}>
                        <Icon name="tag" size={11} color="#9B59FF" />
                        <span style={{ fontSize: 13, color: "#9B59FF", fontWeight: 700 }}>
                            {formatPrice(item.price)} <span style={{ fontSize: 9.5, fontWeight: 600 }}>{t.currency}</span>
                        </span>
                    </div>
                    <div style={{ marginTop: 5 }}>
                        <StatusBadge isCurrent={isCurrent} t={t} />
                    </div>
                </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#64748B" }}>
                    <Icon name="calendar" size={12} color="#94A3B8" />
                    {from} {to ? `— ${to}` : ""}
                </div>
                {item.description && (
                    <div style={{ fontSize: 11, color: "#64748B", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {item.description}
                    </div>
                )}
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                <button
                    onClick={() => onEdit(item)}
                    disabled={!canEdit}
                    title={!isCurrent ? t.expiredEditTooltip : !canEdit ? t.notTodayEditTooltip : t.edit}
                    style={{
                        flex: 1, height: 34, borderRadius: 9, border: "1px solid #E8ECF4", background: "#fff",
                        color: canEdit ? "#1E293B" : "#B6BECC", fontSize: 11, fontWeight: 600, fontFamily: FONT,
                        cursor: canEdit ? "pointer" : "not-allowed",
                        opacity: canEdit ? 1 : 0.6,
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    }}
                >
                    <Icon name={canEdit ? "edit" : "lock"} size={12} color={canEdit ? "#6C3DFF" : "#B6BECC"} />
                    {t.edit}
                </button>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  نمای فهرستی (جدول) اعلامیه‌های قیمت — با ستون‌های قابل‌سورت                */
/* -------------------------------------------------------------------------- */
const LIST_GRID_COLUMNS = "1fr 110px 100px 100px 1.3fr 90px 60px";

function SortableHeaderCell({ label, sortKey, sortConfig, onSort, sortable = true }) {
    const isActive = sortable && sortConfig?.key === sortKey;
    const dir = isActive ? sortConfig.direction : null;
    return (
        <button
            type="button"
            onClick={sortable ? () => onSort(sortKey) : undefined}
            disabled={!sortable}
            style={{
                display: "flex", alignItems: "center", gap: 4, background: "none", border: "none",
                padding: 0, cursor: sortable ? "pointer" : "default",
                fontSize: 10.5, fontWeight: 700, color: isActive ? "#6C3DFF" : "#94A3B8",
                fontFamily: FONT, whiteSpace: "nowrap",
            }}
        >
            {label}
            {sortable && (
                <Icon
                    name={dir === "asc" ? "sortAsc" : dir === "desc" ? "sortDesc" : "sortNeutral"}
                    size={11}
                    color={isActive ? "#6C3DFF" : "#C7CEDA"}
                />
            )}
        </button>
    );
}

function PriceListView({ items, onEdit, t, lang, sortConfig, onSort, maxHeight = 480 }) {
    const columns = [
        { key: "productName", label: t.product },
        { key: "price", label: t.price },
        { key: "fromDate", label: t.fromDate },
        { key: "toDate", label: t.toDate },
        { key: "description", label: t.description },
        { key: "isCurrent", label: t.status },
        { key: "actions", label: t.actions, sortable: false },
    ];
    return (
        <div style={{
            background: "#fff", borderTopLeftRadius: 16, borderTopRightRadius: 16,
            border: "1px solid #EEF2FF", borderBottom: "none",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
            overflow: "hidden",
            display: "flex", flexDirection: "column",
        }}>
            {/* بخش قابل اسکرول افقی برای هدر و ردیف‌ها با هم، اما اسکرول عمودی فقط روی ردیف‌ها */}
            <div style={{ overflowX: "auto" }}>
                <div style={{ minWidth: 760 }}>
                    <div style={{
                        display: "grid", gridTemplateColumns: LIST_GRID_COLUMNS,
                        padding: "12px 16px", background: "#F8FAFF", borderBottom: "1px solid #F1F5F9",
                    }}>
                        {columns.map((col) => (
                            <SortableHeaderCell
                                key={col.key}
                                label={col.label}
                                sortKey={col.key}
                                sortConfig={sortConfig}
                                onSort={onSort}
                                sortable={col.sortable !== false}
                            />
                        ))}
                    </div>
                </div>
            </div>

            {/* بدنه جدول: فقط همین بخش اسکرول عمودی می‌خورد، نه کل صفحه */}
            <div style={{ overflowY: "auto", overflowX: "auto", maxHeight }}>
                <div style={{ minWidth: 760 }}>

                <div>
                    {items.map((item, idx) => {
                        const isCurrent = !item.toDate;
                        const canEdit = canEditPriceItem(item);
                        const from = formatDisplayDate(item.fromDate, lang);
                        const to = formatDisplayDate(item.toDate, lang);
                        return (
                            <div
                                key={item.id}
                                className="price-row"
                                style={{
                                    display: "grid", gridTemplateColumns: LIST_GRID_COLUMNS,
                                    padding: "12px 16px", alignItems: "center",
                                    borderBottom: "1px solid #F5F7FF", transition: "background .15s",
                                    background: idx % 2 === 1 ? "#EEF1F7" : "#fff",
                                }}
                            >
                                <span style={{ fontSize: 12, fontWeight: 700, color: "#1E293B", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {item.productName || t.noValue}
                                    {item.productCode && (
                                        <span style={{ fontSize: 10, fontWeight: 600, color: "#94A3B8" }}> ({item.productCode})</span>
                                    )}
                                </span>

                                <span style={{ fontSize: 11.5, color: "#9B59FF", fontWeight: 700, whiteSpace: "nowrap" }}>
                                    {formatPrice(item.price)} <span style={{ fontSize: 9.5, fontWeight: 600 }}>{t.currency}</span>
                                </span>

                                <span style={{ fontSize: 11, color: "#64748B", whiteSpace: "nowrap" }}>
                                    {from || t.noValue}
                                </span>

                                <span style={{ fontSize: 11, color: "#64748B", whiteSpace: "nowrap" }}>
                                    {to || t.noValue}
                                </span>

                                <span style={{ fontSize: 11, color: "#64748B", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {item.description || t.noValue}
                                </span>

                                <StatusBadge isCurrent={isCurrent} t={t} />

                                <button
                                    onClick={() => onEdit(item)}
                                    disabled={!canEdit}
                                    title={!isCurrent ? t.expiredEditTooltip : !canEdit ? t.notTodayEditTooltip : t.edit}
                                    style={{
                                        justifySelf: "start",
                                        width: 30, height: 30, borderRadius: 8, border: "1px solid #E8ECF4",
                                        background: "#fff", cursor: canEdit ? "pointer" : "not-allowed",
                                        opacity: canEdit ? 1 : 0.6,
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                    }}
                                >
                                    <Icon name={canEdit ? "edit" : "lock"} size={13} color={canEdit ? "#6C3DFF" : "#B6BECC"} />
                                </button>
                            </div>
                        );
                    })}
                </div>
                </div>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  تاگل نمای کارت / فهرست                                                     */
/* -------------------------------------------------------------------------- */
function ViewToggle({ mode, onChange, t }) {
    const options = [
        { key: "list", label: t.viewList, icon: "list" },
        { key: "card", label: t.viewCard, icon: "grid" },
    ];
    return (
        <div style={{ display: "flex", gap: 2, background: "#F1F5F9", borderRadius: 10, padding: 3 }}>
            {options.map((opt) => {
                const active = mode === opt.key;
                return (
                    <button
                        key={opt.key}
                        type="button"
                        onClick={() => onChange(opt.key)}
                        style={{
                            display: "flex", alignItems: "center", gap: 6,
                            height: 32, padding: "0 12px", borderRadius: 8, border: "none",
                            fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer",
                            background: active ? "#fff" : "transparent",
                            color: active ? "#6C3DFF" : "#94A3B8",
                            boxShadow: active ? "0 1px 4px rgba(15,23,42,0.08)" : "none",
                            transition: "all .15s",
                            whiteSpace: "nowrap",
                        }}
                    >
                        <Icon name={opt.icon} size={13} color={active ? "#6C3DFF" : "#94A3B8"} />
                        {opt.label}
                    </button>
                );
            })}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  نوار صفحه‌بندی (پیجینگ)                                                    */
/* -------------------------------------------------------------------------- */
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

function PaginationBar({ page, totalPages, pageSize, onPageChange, onPageSizeChange, rangeStart, rangeEnd, total, t }) {
    const goTo = (p) => {
        const clamped = Math.min(Math.max(1, p), totalPages);
        if (clamped !== page) onPageChange(clamped);
    };

    // شماره صفحات قابل نمایش (حداکثر ۵ عدد، با اعداد کناری اگر لازم بود)
    const pageNumbers = useMemo(() => {
        const arr = [];
        const maxButtons = 5;
        let start = Math.max(1, page - Math.floor(maxButtons / 2));
        let end = Math.min(totalPages, start + maxButtons - 1);
        start = Math.max(1, end - maxButtons + 1);
        for (let i = start; i <= end; i++) arr.push(i);
        return arr;
    }, [page, totalPages]);

    return (
        <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            flexWrap: "wrap", gap: 12,
            padding: "12px 16px", background: "#fff",
            borderTop: "1px solid #F1F5F9",
            borderBottomLeftRadius: 16, borderBottomRightRadius: 16,
        }}>
            {/* راست: متن نمایش + انتخاب تعداد در هر صفحه */}
            <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, color: "#94A3B8", whiteSpace: "nowrap" }}>
                    {t.showingRange(rangeStart, rangeEnd, total)}
                </span>

                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 11, color: "#64748B", whiteSpace: "nowrap" }}>{t.rowsPerPage}</span>
                    <div style={{ position: "relative" }}>
                        <select
                            value={pageSize}
                            onChange={(e) => onPageSizeChange(Number(e.target.value))}
                            style={{
                                height: 32, borderRadius: 9, border: "1px solid #E8ECF4",
                                background: "#fff", color: "#1E293B", fontSize: 11.5, fontWeight: 600,
                                fontFamily: FONT, padding: "0 10px", cursor: "pointer",
                                appearance: "none", WebkitAppearance: "none", MozAppearance: "none",
                                minWidth: 64,
                            }}
                        >
                            {PAGE_SIZE_OPTIONS.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* چپ: دکمه‌های قبلی / شماره صفحه / بعدی */}
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <button
                    type="button"
                    onClick={() => goTo(page - 1)}
                    disabled={page <= 1}
                    style={{
                        height: 32, padding: "0 14px", borderRadius: 9, border: "1px solid #E8ECF4",
                        background: "#fff", color: page <= 1 ? "#CBD5E1" : "#475569",
                        fontSize: 11, fontWeight: 600, fontFamily: FONT,
                        cursor: page <= 1 ? "not-allowed" : "pointer", whiteSpace: "nowrap",
                    }}
                >
                    {t.prev}
                </button>

                {pageNumbers.map((num) => {
                    const active = num === page;
                    return (
                        <button
                            key={num}
                            type="button"
                            onClick={() => goTo(num)}
                            style={{
                                minWidth: 32, height: 32, borderRadius: 9, border: "1px solid",
                                borderColor: active ? "#6C3DFF" : "#E8ECF4",
                                background: active ? "#6C3DFF" : "#fff",
                                color: active ? "#fff" : "#475569",
                                fontSize: 11.5, fontWeight: 700, fontFamily: FONT, cursor: "pointer",
                            }}
                        >
                            {num}
                        </button>
                    );
                })}

                <button
                    type="button"
                    onClick={() => goTo(page + 1)}
                    disabled={page >= totalPages}
                    style={{
                        height: 32, padding: "0 14px", borderRadius: 9, border: "1px solid #E8ECF4",
                        background: "#fff", color: page >= totalPages ? "#CBD5E1" : "#475569",
                        fontSize: 11, fontWeight: 600, fontFamily: FONT,
                        cursor: page >= totalPages ? "not-allowed" : "pointer", whiteSpace: "nowrap",
                    }}
                >
                    {t.next}
                </button>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  بنر گزارش «کالاهای بدون قیمت جاری»                                        */
/* -------------------------------------------------------------------------- */
function NoPriceReportBanner({ list, t, onExport, exporting }) {
    const count = list.length;
    if (count === 0) {
        return (
            <div style={{
                display: "flex", alignItems: "center", gap: 10, background: "#10B98110",
                border: "1px solid #10B98130", borderRadius: 14, padding: "12px 16px", marginBottom: 14,
            }}>
                <Icon name="check" size={15} color="#10B981" />
                <span style={{ fontSize: 11.5, color: "#0F766E", fontWeight: 600 }}>{t.noPriceReportNone}</span>
            </div>
        );
    }
    return (
        <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap",
            background: "#F59E0B10", border: "1px solid #F59E0B30", borderRadius: 14, padding: "12px 16px", marginBottom: 14,
        }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{
                    width: 34, height: 34, borderRadius: 10, flexShrink: 0,
                    background: "#F59E0B1F", display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                    <Icon name="alertTriangle" size={16} color="#F59E0B" />
                </div>
                <div>
                    <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: "#92400E" }}>{t.noPriceReportTitle}</p>
                    <p style={{ margin: "2px 0 0", fontSize: 11, color: "#B45309" }}>{t.noPriceReportDesc(count)}</p>
                </div>
            </div>
            <button
                onClick={onExport}
                disabled={exporting}
                style={{
                    display: "flex", alignItems: "center", gap: 6, height: 34, padding: "0 14px",
                    borderRadius: 9, border: "1px solid #F59E0B40", background: "#fff", color: "#B45309",
                    fontSize: 11, fontWeight: 700, fontFamily: FONT, cursor: exporting ? "not-allowed" : "pointer",
                    opacity: exporting ? 0.6 : 1, whiteSpace: "nowrap",
                }}
            >
                <Icon name="download" size={12} color="#B45309" />
                {exporting ? t.exportingExcel : t.exportNoPriceExcel}
            </button>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  خروجی اکسل — استایل‌دار و مناسب برای ساخت جدول Pivot                      */
/* -------------------------------------------------------------------------- */
const EXCEL_HEADER_FILL = "FF6C3DFF";
const EXCEL_STRIPE_FILL = "FFF8FAFF";
const EXCEL_CURRENT_FILL = "FFE8FBF3";
const EXCEL_EXPIRED_FILL = "FFF1F3F6";

async function downloadWorkbook(workbook, filename) {
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
}

function styleHeaderRow(row) {
    row.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: EXCEL_HEADER_FILL } };
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.border = {
            top: { style: "thin", color: { argb: "FF6C3DFF" } },
            bottom: { style: "thin", color: { argb: "FF6C3DFF" } },
        };
    });
    row.height = 22;
}

/* خروجی اکسل اعلامیه‌های قیمت — فلَت و مناسب برای Pivot Table */
/* خروجی اکسل اعلامیه‌های قیمت — فلَت و مناسب برای Pivot Table */
async function exportPricesToExcel(items, t, lang, products) {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = "Product Price Management";
    workbook.created = new Date();

    const sheet = workbook.addWorksheet(t.excelSheetPrices, {
        views: [{ rightToLeft: lang === "fa" || lang === "ar", state: "frozen", ySplit: 1 }],
    });

    sheet.columns = [
        { header: t.excelColProduct, key: "productName", width: 28 },
        { header: t.excelColCode, key: "productCode", width: 16 },
        { header: `${t.excelColPrice} (${t.currency})`, key: "price", width: 16 },
        { header: t.excelColFromDate, key: "fromDate", width: 16 },
        { header: t.excelColToDate, key: "toDate", width: 16 },
        { header: t.excelColStatus, key: "statusLabel", width: 14 },
        { header: t.excelColDescription, key: "description", width: 34 },
    ];

    items.forEach((item) => {
        const isCurrent = !item.toDate;
        const product = products.find((p) => p.id === item.productId);
        const productCode = item.productCode || product?.code || "";
        sheet.addRow({
            productName: item.productName || "",
            productCode,
            price: Number(item.price) || 0,
            fromDate: formatDisplayDate(item.fromDate, lang) || "",
            toDate: formatDisplayDate(item.toDate, lang) || "",
            statusLabel: isCurrent ? t.current : t.expired,
            description: item.description || "",
        });
    });

    styleHeaderRow(sheet.getRow(1));

    sheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return;
        const item = items[rowNumber - 2];
        const isCurrent = item && !item.toDate;

        row.getCell(3).numFmt = "#,##0";
        row.getCell(4).alignment = { vertical: "middle", horizontal: "center" };
        row.getCell(5).alignment = { vertical: "middle", horizontal: "center" };
        row.alignment = { vertical: "middle" };
        row.height = 19;

        const stripe = rowNumber % 2 === 0 ? EXCEL_STRIPE_FILL : "FFFFFFFF";
        row.eachCell((cell) => {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: stripe } };
            cell.border = { bottom: { style: "hair", color: { argb: "FFE8ECF4" } } };
        });

        const statusCell = row.getCell(6);
        statusCell.font = { bold: true, color: { argb: isCurrent ? "FF10B981" : "FF94A3B8" } };
        statusCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: isCurrent ? EXCEL_CURRENT_FILL : EXCEL_EXPIRED_FILL } };
        statusCell.alignment = { vertical: "middle", horizontal: "center" };
    });

    sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: sheet.columns.length } };
    sheet.views = [{ ...sheet.views[0], state: "frozen", ySplit: 1 }];

    await downloadWorkbook(workbook, `price-announcements-${getTodayISO()}.xlsx`);
}

/* خروجی اکسل کالاهای بدون قیمت جاری */
async function exportNoPriceProductsToExcel(list, t, lang) {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = "Product Price Management";
    workbook.created = new Date();

    const sheet = workbook.addWorksheet(t.excelSheetNoPrice, {
        views: [{ rightToLeft: lang === "fa" || lang === "ar" }],
    });

    sheet.columns = [
        { header: t.excelColProduct, key: "productName", width: 30 },
        { header: t.excelColCode, key: "productCode", width: 16 },
        { header: t.excelColLastPriceInfo, key: "lastInfo", width: 26 },
        { header: t.excelColLastPriceDate, key: "lastDate", width: 16 },
    ];

    list.forEach((row) => {
        sheet.addRow({
            productName: row.name || "",
            productCode: row.code || "",
            lastInfo: row.lastPrice ? t.lastPriceExpiredOn : t.neverPriced,
            lastDate: row.lastPrice?.toDate ? formatDisplayDate(row.lastPrice.toDate, lang) || "" : "",
        });
    });

    styleHeaderRow(sheet.getRow(1));

    sheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return;
        row.getCell(4).numFmt = "yyyy-mm-dd";
        row.alignment = { vertical: "middle" };
        row.height = 19;
        const stripe = rowNumber % 2 === 0 ? EXCEL_STRIPE_FILL : "FFFFFFFF";
        row.eachCell((cell) => {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: stripe } };
            cell.border = { bottom: { style: "hair", color: { argb: "FFE8ECF4" } } };
        });
    });

    sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: sheet.columns.length } };

    // شیت خلاصه کوچک برای مرجع سریع (تعداد کل)
    const summary = workbook.addWorksheet(t.excelGeneratedAt);
    summary.addRow([t.excelGeneratedAt, new Date().toLocaleString(lang === "fa" ? "fa-IR" : "ar")]);
    summary.addRow([t.noPriceReportTitle, list.length]);
    summary.getColumn(1).width = 26;
    summary.getColumn(2).width = 22;

    await downloadWorkbook(workbook, `products-without-price-${getTodayISO()}.xlsx`);
}

/* -------------------------------------------------------------------------- */
/*  کامپوننت اصلی                                                              */
/* -------------------------------------------------------------------------- */
export default function ProductPriceManagement() {
    const { lang } = useLanguage();
    const t = TEXTS[lang] || TEXTS.fa;

    const [prices, setPrices] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState({ visible: false, message: "", type: "success" });
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all"); // all | current | expired
    const [viewMode, setViewMode] = useState("card"); // card | list
    const [modal, setModal] = useState(null); // {mode, loadingData, data:{id, productId, price, fromDate, toDate, description}}
    const [saving, setSaving] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [sortConfig, setSortConfig] = useState({ key: "fromDate", direction: "desc" });
    const [exportingPrices, setExportingPrices] = useState(false);
    const [exportingNoPrice, setExportingNoPrice] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    const showToast = (message, type = "success") => {
        setToast({ visible: true, message, type });
        setTimeout(() => setToast({ visible: false, message: "", type: "success" }), 3000);
    };

    const fetchPrices = async () => {
        try {
            const res = await GetAllProductPrice();
            const raw = res.data?.data ?? res.data ?? [];
            setPrices(Array.isArray(raw) ? raw.map(normalizeProductPrice) : []);
        } catch (err) {
            showToast(extractServerErrorMessage(err) || t.toastFetchFail, "error");
        }
    };

    const fetchProducts = async () => {
        try {
            const res = await GetActiveProduct();
            const raw = res.data?.data ?? res.data ?? [];
            setProducts(Array.isArray(raw) ? raw.map(normalizeProduct) : []);
        } catch (err) {
            showToast(extractServerErrorMessage(err) || t.toastProductsFetchFail, "error");
        }
    };

    useEffect(() => {
        (async () => {
            setLoading(true);
            await Promise.all([fetchPrices(), fetchProducts()]);
            setLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtered = useMemo(() => {
        let list = prices;
        if (statusFilter !== "all") {
            const wantCurrent = statusFilter === "current";
            list = list.filter((p) => (!p.toDate) === wantCurrent);
        }
        if (searchTerm.trim()) {
            const lower = searchTerm.trim().toLowerCase();
            list = list.filter(
                (p) =>
                    (p.productName || "").toLowerCase().includes(lower) ||
                    (p.description || "").toLowerCase().includes(lower)
            );
        }
        return list;
    }, [prices, statusFilter, searchTerm]);

    /* اعمال سورت روی لیست فیلترشده — برای هر دو نمای کارت و فهرست */
    const sortedFiltered = useMemo(() => {
        if (!sortConfig?.key) return filtered;
        const { key, direction } = sortConfig;
        const dir = direction === "asc" ? 1 : -1;

        const getValue = (item) => {
            switch (key) {
                case "productName":
                    return (item.productName || "").toLowerCase();
                case "price":
                    return Number(item.price) || 0;
                case "fromDate":
                    return item.fromDate ? new Date(item.fromDate).getTime() : -Infinity;
                case "toDate":
                    return item.toDate ? new Date(item.toDate).getTime() : Infinity; // بدون تاریخ پایان = جاری‌ترین
                case "description":
                    return (item.description || "").toLowerCase();
                case "isCurrent":
                    return item.toDate ? 0 : 1;
                default:
                    return 0;
            }
        };

        return [...filtered].sort((a, b) => {
            const va = getValue(a);
            const vb = getValue(b);
            if (va < vb) return -1 * dir;
            if (va > vb) return 1 * dir;
            return 0;
        });
    }, [filtered, sortConfig]);

    /* بازنشانی صفحه به ۱ با تغییر فیلتر، جستجو یا تعداد در هر صفحه */
    useEffect(() => {
        setPage(1);
    }, [statusFilter, searchTerm, pageSize]);

    const totalItems = sortedFiltered.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    const paginatedItems = useMemo(() => {
        const start = (page - 1) * pageSize;
        return sortedFiltered.slice(start, start + pageSize);
    }, [sortedFiltered, page, pageSize]);

    const pageRangeStart = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
    const pageRangeEnd = Math.min(page * pageSize, totalItems);

    const handleSort = (key) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
            }
            return { key, direction: "asc" };
        });
    };

    const counts = useMemo(() => ({
        all: prices.length,
        current: prices.filter((p) => !p.toDate).length,
        expired: prices.filter((p) => !!p.toDate).length,
    }), [prices]);

    /* گزارش کالاهای بدون قیمت جاری: محصولاتی که هیچ اعلامیه‌ی جاری (بدون تاریخ پایان) ندارند */
    const productsWithoutCurrentPrice = useMemo(() => {
        return products
            .filter((pr) => !prices.some((p) => p.productId === pr.id && !p.toDate))
            .map((pr) => {
                const productPrices = prices
                    .filter((p) => p.productId === pr.id)
                    .sort((a, b) => new Date(b.fromDate) - new Date(a.fromDate));
                return { ...pr, lastPrice: productPrices[0] || null };
            });
    }, [products, prices]);

    const productOptions = useMemo(
        () => products.map((p) => ({ value: p.id, label: p.code ? `${p.name} (${p.code})` : p.name })),
        [products]
    );

    const emptyFormData = {
        id: null, productId: null, price: "", fromDate: getTodayISO(), toDate: "", description: "",
    };

    const openAddModal = () => {
        setFieldErrors({});
        setModal({ mode: "add", loadingData: false, data: { ...emptyFormData } });
    };

    const openEditModal = async (item) => {
        // اعلامیه‌های منقضی‌شده (دارای تاریخ پایان) قابل ویرایش نیستند
        if (item.toDate) {
            showToast(t.toastCannotEditExpired, "error");
            return;
        }
        // اعلامیه جاری فقط در همان روزی که ثبت شده قابل ویرایش است
        if (!canEditPriceItem(item)) {
            showToast(t.toastCannotEditNotToday, "error");
            return;
        }
        setFieldErrors({});
        setModal({
            mode: "edit", loadingData: true,
            data: { ...item, price: String(item.price ?? ""), fromDate: toInputDate(item.fromDate), toDate: toInputDate(item.toDate) },
        });
        try {
            const res = await GetProductPriceById(item.id);
            const raw = res.data?.data ?? res.data;
            if (raw) {
                const fresh = normalizeProductPrice(raw);
                // اگر بین باز کردن مودال و دریافت داده‌ی تازه، این اعلامیه منقضی شده باشد، ویرایش را متوقف کن
                if (fresh.toDate) {
                    setModal(null);
                    showToast(t.toastCannotEditExpired, "error");
                    return;
                }
                // یا اگر دیگر «امروز» نیست (مثلاً کاربر مودال را از روز قبل باز نگه داشته)، ویرایش را متوقف کن
                if (!canEditPriceItem(fresh)) {
                    setModal(null);
                    showToast(t.toastCannotEditNotToday, "error");
                    return;
                }
                setModal({
                    mode: "edit", loadingData: false,
                    data: { ...fresh, price: String(fresh.price ?? ""), fromDate: toInputDate(fresh.fromDate), toDate: toInputDate(fresh.toDate) },
                });
            } else {
                setModal((m) => (m ? { ...m, loadingData: false } : m));
            }
        } catch (err) {
            setModal(null);
            showToast(extractServerErrorMessage(err) || t.toastFetchFail, "error");
        }
    };

    const closeModal = () => { if (!saving) setModal(null); };

    const extractServerErrorMessage = (err) => {
        const data = err?.response?.data;
        if (typeof data === "string" && data.trim()) return data.trim();
        if (data && typeof data === "object") {
            return data.message || data.title || data.error || null;
        }
        return null;
    };

    const isDateOrderMessage = (msg) =>
        !!msg && (msg.includes("باید بعد از اعلامیه قبلی") || msg.includes("بعد الإعلان السابق"));

    const handleSubmit = async () => {
        if (!modal) return;
        const errors = {};
        if (!modal.data.productId) errors.productId = t.toastProductRequired;
        const priceNum = Number(modal.data.price);
        if (!modal.data.price || Number.isNaN(priceNum) || priceNum < 0) errors.price = t.toastPriceRequired;
        if (!modal.data.fromDate) {
            errors.fromDate = t.toastFromDateRequired;
        } else if (modal.mode === "add" && modal.data.fromDate < getTodayISO()) {
            // در حالت افزودن، تاریخ شروع نمی‌تواند قبل از امروز باشد
            errors.fromDate = t.toastFromDatePast;
        }

        // قانون جدید ۱: تاریخ شروع و پایان یک اعلامیه نمی‌توانند در یک روز باشند
        if (!errors.fromDate && modal.data.fromDate && modal.data.toDate && isSameDay(modal.data.fromDate, modal.data.toDate)) {
            errors.toDate = t.toastSameDayRange;
        }

        // قانون جدید ۲: برای یک محصول، در یک روز نباید دو اعلامیه قیمت با تاریخ شروع یکسان وجود داشته باشد
        if (!errors.fromDate && !errors.productId && modal.data.productId && modal.data.fromDate) {
            const hasDuplicateSameDay = prices.some(
                (p) =>
                    p.productId === modal.data.productId &&
                    p.id !== modal.data.id &&
                    isSameDay(p.fromDate, modal.data.fromDate)
            );
            if (hasDuplicateSameDay) {
                errors.fromDate = t.toastDuplicateSameDay;
            }
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            showToast(errors.productId || errors.price || errors.fromDate || errors.toDate, "error");
            return;
        }
        setFieldErrors({});

        const payload = {
            ...(modal.mode === "edit" ? { id: modal.data.id } : {}),
            productId: modal.data.productId,
            price: priceNum,
            fromDate: modal.data.fromDate,
            toDate: modal.mode === "edit" ? (modal.data.toDate || null) : null,
            description: modal.data.description?.trim() || null,
        };

        setSaving(true);
        try {
            if (modal.mode === "add") {
                await AddProductPrice(payload);
                showToast(t.toastAddSuccess);
            } else {
                await UpdateProductPrice(payload);
                showToast(t.toastUpdateSuccess);
            }
            setModal(null);
            await fetchPrices();
            // ✅ اصلاح‌شده
        } catch (err) {
            console.error("خطای ذخیره قیمت محصول:", err, err?.response?.data);
            const serverMsg = extractServerErrorMessage(err);
            if (isDateOrderMessage(serverMsg)) {
                setFieldErrors({ fromDate: serverMsg });
                showToast(serverMsg, "error");
            } else {
                showToast(serverMsg || (modal.mode === "add" ? t.toastAddFail : t.toastUpdateFail), "error");
            }
        } finally {
            setSaving(false);
        }
    };

    const handleExportPrices = async () => {
        setExportingPrices(true);
        try {
            await exportPricesToExcel(sortedFiltered, t, lang, products);
        } catch (err) {
            console.error("خطای خروجی اکسل:", err);
            showToast(t.toastFetchFail, "error");
        } finally {
            setExportingPrices(false);
        }
    };

    const handleExportNoPrice = async () => {
        setExportingNoPrice(true);
        try {
            await exportNoPriceProductsToExcel(productsWithoutCurrentPrice, t, lang);
        } catch (err) {
            console.error("خطای خروجی اکسل کالاهای بدون قیمت:", err);
            showToast(t.toastFetchFail, "error");
        } finally {
            setExportingNoPrice(false);
        }
    };

    const filterTabs = [
        { key: "all", label: t.filterAll, color: "#6C3DFF" },
        { key: "current", label: t.current, color: "#10B981" },
        { key: "expired", label: t.expired, color: "#94A3B8" },
    ];

    return (
        <div style={{ fontFamily: FONT, direction: t.dir, minHeight: "100vh", background: "#F5F6FA" }}>
            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes fadeUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
                @keyframes toastDrop { from { opacity:0; transform:translate(-50%,-12px); } to { opacity:1; transform:translate(-50%,0); } }
                .price-card:hover { box-shadow: 0 8px 24px rgba(108,61,255,0.10); transform: translateY(-2px); border-color: #E4DBFF; }
                .price-row:hover { background: #FAFAFF !important; }
            `}</style>

            {/* هدر */}
            <div style={{
                background: "linear-gradient(135deg, #6C3DFF 0%, #9B59FF 100%)",
                padding: "26px 28px 84px", position: "relative", overflow: "hidden",
            }}>
                <div style={{ position: "absolute", top: -60, left: -60, width: 220, height: 220, borderRadius: "50%", background: "rgba(255,255,255,0.06)" }} />
                <div style={{ position: "absolute", bottom: -40, right: 80, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, position: "relative", zIndex: 1 }}>
                    <div>
                        <h1 style={{ fontSize: 19, fontWeight: 700, color: "#fff", margin: 0 }}>{t.title}</h1>
                        <p style={{ fontSize: 11, color: "rgba(255,255,255,0.65)", margin: "4px 0 0" }}>{t.subtitle}</p>
                    </div>

                    <button
                        onClick={openAddModal}
                        style={{
                            height: 40, background: "#fff", border: "none", borderRadius: 12, color: "#6C3DFF",
                            fontSize: 11.5, fontFamily: FONT, fontWeight: 700, cursor: "pointer",
                            padding: "0 16px", display: "flex", alignItems: "center", gap: 6,
                            boxShadow: "0 10px 24px rgba(15,23,42,0.18)", whiteSpace: "nowrap", flexShrink: 0,
                        }}
                    >
                        <Icon name="plus" size={13} color="#6C3DFF" />
                        {t.addNew}
                    </button>
                </div>
            </div>

            {/* باکس جستجو + فیلتر + فهرست/کارت */}
            <div style={{ padding: "0 28px", marginTop: -56, animation: "fadeUp .4s ease both", position: "relative", zIndex: 2 }}>
                <div style={{
                    background: "#fff", borderRadius: 18,
                    border: "1px solid #EEF2FF", boxShadow: "0 8px 28px rgba(108,61,255,0.10)",
                    overflow: "hidden",
                }}>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", padding: "10px 14px", borderBottom: "1px solid #F1F5F9" }}>
                        {filterTabs.map((tab) => {
                            const active = statusFilter === tab.key;
                            return (
                                <button
                                    key={tab.key}
                                    onClick={() => setStatusFilter(tab.key)}
                                    style={{
                                        display: "flex", alignItems: "center", gap: 6,
                                        padding: "7px 12px", borderRadius: 9, border: "none",
                                        cursor: "pointer", fontFamily: FONT,
                                        fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
                                        transition: "all .18s",
                                        background: active ? `linear-gradient(135deg, ${tab.color}, ${tab.color}CC)` : "transparent",
                                        color: active ? "#fff" : "#64748B",
                                        boxShadow: active ? `0 4px 10px ${tab.color}40` : "none",
                                    }}
                                >
                                    {tab.label}
                                    <span style={{
                                        fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 99,
                                        background: active ? "rgba(255,255,255,0.25)" : "#F1F5F9",
                                        color: active ? "#fff" : "#94A3B8",
                                    }}>
                                        {counts[tab.key] ?? 0}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", flexWrap: "wrap" }}>
                        <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
                            <input
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder={t.searchPlaceholder}
                                style={{ ...fieldBaseStyle, padding: "0 36px 0 12px" }}
                            />
                            <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}>
                                <Icon name="search" size={13} color="#94A3B8" />
                            </span>
                        </div>

                        <span style={{ fontSize: 10, color: "#94A3B8", whiteSpace: "nowrap" }}>{t.itemsCount(filtered.length)}</span>

                        <button
                            onClick={handleExportPrices}
                            disabled={exportingPrices || sortedFiltered.length === 0}
                            style={{
                                display: "flex", alignItems: "center", gap: 6, height: 34, padding: "0 14px",
                                borderRadius: 9, border: "1px solid #E8ECF4", background: "#fff", color: "#1E293B",
                                fontSize: 11, fontWeight: 700, fontFamily: FONT,
                                cursor: exportingPrices || sortedFiltered.length === 0 ? "not-allowed" : "pointer",
                                opacity: sortedFiltered.length === 0 ? 0.5 : 1, whiteSpace: "nowrap",
                            }}
                        >
                            {exportingPrices ? (
                                <div style={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid #EDE9FF", borderTopColor: "#6C3DFF", animation: "spin .7s linear infinite" }} />
                            ) : (
                                <Icon name="download" size={13} color="#10B981" />
                            )}
                            {exportingPrices ? t.exportingExcel : t.exportExcel}
                        </button>

                        <ViewToggle mode={viewMode} onChange={setViewMode} t={t} />
                    </div>
                </div>
            </div>

            {/* گرید / جدول اعلامیه‌های قیمت */}
            <div style={{ padding: "18px 28px 40px", animation: "fadeUp .5s .1s ease both" }}>
                {!loading && (
                    <NoPriceReportBanner
                        list={productsWithoutCurrentPrice}
                        t={t}
                        onExport={handleExportNoPrice}
                        exporting={exportingNoPrice}
                    />
                )}

                {loading ? (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "56px 0" }}>
                        <div style={{ width: 36, height: 36, borderRadius: "50%", border: "3px solid #EDE9FF", borderTopColor: "#6C3DFF", animation: "spin .8s linear infinite", marginBottom: 12 }} />
                        <p style={{ color: "#94A3B8", fontSize: 12 }}>{t.loading}</p>
                    </div>
                ) : filtered.length === 0 ? (
                    <div style={{
                        background: "#fff", borderRadius: 18, border: "1px solid #EEF2FF",
                        display: "flex", flexDirection: "column", alignItems: "center", padding: "46px 0", gap: 12,
                    }}>
                        <div style={{ width: 48, height: 48, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", background: "#6C3DFF14" }}>
                            <Icon name="tag" size={20} color="#6C3DFF" />
                        </div>
                        <p style={{ color: "#94A3B8", fontSize: 12, margin: 0 }}>
                            {searchTerm.trim() || statusFilter !== "all" ? t.noSearchResult : t.empty}
                        </p>
                        {!searchTerm.trim() && statusFilter === "all" && (
                            <button
                                onClick={openAddModal}
                                style={{
                                    background: "#6C3DFF14", color: "#6C3DFF", border: "none", borderRadius: 10,
                                    padding: "7px 16px", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: FONT,
                                }}
                            >
                                {t.emptyAction}
                            </button>
                        )}
                    </div>
                ) : viewMode === "card" ? (
                    <div style={{
                        background: "#fff", borderRadius: 16, border: "1px solid #EEF2FF",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.03)", overflow: "hidden",
                        display: "flex", flexDirection: "column",
                    }}>
                        <div style={{ maxHeight: 560, overflowY: "auto", padding: 14 }}>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 14 }}>
                                {paginatedItems.map((item) => (
                                    <PriceCard key={item.id} item={item} onEdit={openEditModal} t={t} lang={lang} />
                                ))}
                            </div>
                        </div>
                        <PaginationBar
                            page={page}
                            totalPages={totalPages}
                            pageSize={pageSize}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            rangeStart={pageRangeStart}
                            rangeEnd={pageRangeEnd}
                            total={totalItems}
                            t={t}
                        />
                    </div>
                ) : (
                    <>
                        <PriceListView
                            items={paginatedItems}
                            onEdit={openEditModal}
                            t={t}
                            lang={lang}
                            sortConfig={sortConfig}
                            onSort={handleSort}
                            maxHeight={480}
                        />
                        <div style={{
                            background: "#fff", border: "1px solid #EEF2FF",
                            borderBottomLeftRadius: 16, borderBottomRightRadius: 16,
                        }}>
                            <PaginationBar
                                page={page}
                                totalPages={totalPages}
                                pageSize={pageSize}
                                onPageChange={setPage}
                                onPageSizeChange={setPageSize}
                                rangeStart={pageRangeStart}
                                rangeEnd={pageRangeEnd}
                                total={totalItems}
                                t={t}
                            />
                        </div>
                    </>
                )}
            </div>

            {/* مودال افزودن / ویرایش اعلامیه قیمت */}
            {modal && (
                <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1500, padding: 16 }}>
                    <div style={{ background: "#fff", borderRadius: 18, width: "min(420px, 100%)", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(15,23,42,.25)" }}>
                        <div style={{ padding: "16px 20px", borderBottom: "1px solid #F1F5F9", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                                <span style={{ width: 26, height: 26, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", background: "#6C3DFF14" }}>
                                    <Icon name="tag" size={12} color="#6C3DFF" />
                                </span>
                                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#1E293B" }}>
                                    {modal.mode === "add" ? t.modalAddTitle : t.modalEditTitle}
                                </p>
                            </div>
                            <button onClick={closeModal} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
                                <Icon name="close" size={16} color="#94A3B8" />
                            </button>
                        </div>

                        <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
                            {modal.loadingData ? (
                                <div style={{ display: "flex", justifyContent: "center", padding: "18px 0" }}>
                                    <div style={{ width: 26, height: 26, borderRadius: "50%", border: "3px solid #EDE9FF", borderTopColor: "#6C3DFF", animation: "spin .8s linear infinite" }} />
                                </div>
                            ) : (
                                <>
                                    <div>
                                        <label style={{ fontSize: 10, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>{t.product}</label>
                                        <SearchableSelect
                                            value={modal.data.productId}
                                            onChange={(id) => {
                                                setModal((m) => ({ ...m, data: { ...m.data, productId: id } }));
                                                if (fieldErrors.productId) setFieldErrors((fe) => ({ ...fe, productId: null }));
                                            }}
                                            options={productOptions}
                                            placeholder={t.selectProduct}
                                            searchPlaceholder={t.searchProductPlaceholder}
                                            emptyText={t.noOptionsFound}
                                            hasError={!!fieldErrors.productId}
                                        />
                                    </div>

                                    <div>
                                        <label style={{ fontSize: 10, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>
                                            {t.price} <span style={{ color: "#94A3B8", fontWeight: 500 }}>({t.currency})</span>
                                        </label>
                                        <TextField
                                            type="text"
                                            dir="ltr"
                                            value={formatNumberInput(modal.data.price)}
                                            onChange={(e) => {
                                                const raw = unformatNumberInput(e.target.value);
                                                // فقط رقم و حداکثر یک نقطه اعشار مجاز است
                                                if (/^\d*\.?\d*$/.test(raw)) {
                                                    setModal((m) => ({ ...m, data: { ...m.data, price: raw } }));
                                                    if (fieldErrors.price) setFieldErrors((fe) => ({ ...fe, price: null }));
                                                }
                                            }}
                                            placeholder={t.price}
                                            hasError={!!fieldErrors.price}
                                        />
                                    </div>

                                    <div style={{ display: "flex", gap: 10 }}>
                                        <div style={{ flex: 1 }}>
                                            <label style={{ fontSize: 10, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>{t.fromDate}</label>
                                            {lang === "fa" ? (
                                                <JalaliDatePicker
                                                    value={modal.data.fromDate}
                                                    onChange={(v) => {
                                                        setModal((m) => ({ ...m, data: { ...m.data, fromDate: v } }));
                                                        if (fieldErrors.fromDate) setFieldErrors((fe) => ({ ...fe, fromDate: null }));
                                                    }}
                                                    hasError={!!fieldErrors.fromDate}
                                                    placeholder={t.fromDate}
                                                    minDate={modal.mode === "add" ? getTodayISO() : undefined}
                                                />
                                            ) : (
                                                <TextField
                                                    type="date"
                                                    dir="ltr"
                                                    min={modal.mode === "add" ? getTodayISO() : undefined}
                                                    value={modal.data.fromDate}
                                                    onChange={(e) => {
                                                        setModal((m) => ({ ...m, data: { ...m.data, fromDate: e.target.value } }));
                                                        if (fieldErrors.fromDate) setFieldErrors((fe) => ({ ...fe, fromDate: null }));
                                                    }}
                                                    hasError={!!fieldErrors.fromDate}
                                                />
                                            )}
                                            {fieldErrors.fromDate && (
                                                <p style={{ margin: "6px 0 0", fontSize: 10, color: "#EF4444" }}>{fieldErrors.fromDate}</p>
                                            )}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <label style={{ fontSize: 10, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>{t.toDate}</label>
                                            {lang === "fa" ? (
                                                <JalaliDatePicker
                                                    value={modal.data.toDate}
                                                    onChange={(v) => {
                                                        setModal((m) => ({ ...m, data: { ...m.data, toDate: v } }));
                                                        if (fieldErrors.toDate) setFieldErrors((fe) => ({ ...fe, toDate: null }));
                                                    }}
                                                    disabled={modal.mode === "add"}
                                                    placeholder={t.toDate}
                                                    hasError={!!fieldErrors.toDate}
                                                />
                                            ) : (
                                                <TextField
                                                    type="date"
                                                    dir="ltr"
                                                    disabled={modal.mode === "add"}
                                                    value={modal.data.toDate}
                                                    onChange={(e) => {
                                                        setModal((m) => ({ ...m, data: { ...m.data, toDate: e.target.value } }));
                                                        if (fieldErrors.toDate) setFieldErrors((fe) => ({ ...fe, toDate: null }));
                                                    }}
                                                    hasError={!!fieldErrors.toDate}
                                                />
                                            )}
                                            {fieldErrors.toDate && (
                                                <p style={{ margin: "6px 0 0", fontSize: 10, color: "#EF4444" }}>{fieldErrors.toDate}</p>
                                            )}
                                        </div>
                                    </div>
                                    {modal.mode === "add" && (
                                        <p style={{ margin: 0, fontSize: 10, color: "#94A3B8" }}>{t.autoToDateHint}</p>
                                    )}
                                    {modal.mode === "edit" && (
                                        <p style={{ margin: 0, fontSize: 10, color: "#94A3B8" }}>{t.stillCurrent}</p>
                                    )}

                                    <div>
                                        <label style={{ fontSize: 10, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>{t.description}</label>
                                        <TextField
                                            value={modal.data.description}
                                            onChange={(e) => setModal((m) => ({ ...m, data: { ...m.data, description: e.target.value } }))}
                                            placeholder={t.description}
                                        />
                                    </div>
                                </>
                            )}
                        </div>

                        <div style={{ padding: "14px 20px", borderTop: "1px solid #F1F5F9", display: "flex", justifyContent: "flex-end", gap: 8, position: "sticky", bottom: 0, background: "#fff" }}>
                            <button
                                onClick={closeModal}
                                disabled={saving}
                                style={{
                                    height: 38, padding: "0 16px", borderRadius: 10, border: "1.5px solid #E8ECF4",
                                    background: "#fff", color: "#475569", fontSize: 11.5, fontWeight: 600,
                                    fontFamily: FONT, cursor: "pointer",
                                }}
                            >
                                {t.cancel}
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={saving || modal.loadingData}
                                style={{
                                    height: 38, padding: "0 20px", borderRadius: 10, border: "none",
                                    background: saving ? "#A78BFA" : "#6C3DFF", color: "#fff", fontSize: 11.5, fontWeight: 600,
                                    fontFamily: FONT, cursor: saving ? "not-allowed" : "pointer",
                                    display: "flex", alignItems: "center", gap: 8,
                                }}
                            >
                                {saving && <div style={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid rgba(255,255,255,.4)", borderTopColor: "#fff", animation: "spin .7s linear infinite" }} />}
                                {saving ? t.saving : t.save}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Toast message={toast.message} visible={toast.visible} type={toast.type} />
        </div>
    );
}